import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Button, Icon, Text, pickFiles, pickImageFile } from "@salestrak/ui";
import type { Editor } from "@tiptap/react";
import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import {
  EditorFormatStrip,
  type ToolbarButtonSpec,
} from "#app/components/editor/editor-toolbar";
import { useActivityContext } from "./activity-context";
import { useDockedComposer } from "./docked-composer-context";
import { useIssueViewCapabilities } from "../issue-view-capabilities";
import {
  NoteMediaAttachments,
  type NoteMediaCard,
} from "./note-media-attachments";
import { isRichTextEmpty } from "#app/lib/utils/rich-text-value";
import { extractMentionedUserIds } from "#app/lib/utils/extract-mentions";
import { useMentionAccessActions } from "#app/lib/mention-access-dialog-context";
import { useNoteMediaUpload, useResolvedMediaHtml } from "#app/lib/hooks/use-note-media";
import { usePendingMediaUploads } from "#app/lib/hooks/use-pending-media-uploads";
import { restoreMediaHtml } from "#app/lib/media-content";
import { NOTE_MEDIA_ACCEPT, mediaKind } from "#app/lib/media-kind";
import type { NoteMediaItem } from "#app/components/media/media-attachment-tile";
import { canManageSharing as canManageSharingFn } from "#app/lib/utils/access-level";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import type { Omittable } from "@salestrak/ui";

type NoteComposerProps = Omittable<{
  issueId: string;
  parentNoteId?: string | null;
  editMode?: boolean;
  editNoteId?: string | null;
  initialContent?: string;
  /** Edit mode: the note's posted footer attachments, editable in place. */
  initialMedia?: NoteMediaItem[];
  startExpanded?: boolean;
  /** Focus the editor whenever this changes to a non-zero value. */
  focusRequest?: number;
  "aria-label"?: string;
  /** The phone sheet's bar on the keyboard (Messages): one growing field
   *  with Send inside its trailing edge and a "+" that folds out the tools
   *  above it. No Cancel — leaving keeps the draft. New comments only. */
  docked?: boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
}>;

/**
 * Expandable rich text composer for notes/comments.
 * Supports three modes: new parent note, reply, and edit.
 * Starts collapsed to one line of the editor; focusing it expands the footer.
 */
export function NoteComposer({
  issueId,
  parentNoteId = null,
  editMode = false,
  editNoteId = null,
  initialContent = "",
  initialMedia,
  startExpanded = false,
  focusRequest = 0,
  "aria-label": ariaLabel = "Comment",
  docked = false,
  onCancel,
  onSubmit,
}: NoteComposerProps) {
  const { createNote, updateNote } = useActivityContext("NoteComposer");
  const { canComment, level } = useIssueViewCapabilities();
  const canManageItemSharing = canManageSharingFn(level);
  const { hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);

  // `mountExpanded` keeps the editor mounted while the height animates down;
  // swapping to the short collapsed row in the same commit would leave nothing
  // for the `auto`-height transition to resolve against.
  const [expanded, setExpanded] = useState(editMode || startExpanded);
  const [mountExpanded, setMountExpanded] = useState(editMode || startExpanded);
  const [content, setContent] = useState(initialContent);
  const [submitting, setSubmitting] = useState(false);
  // Footer photo attachments (cards under the editor). Inline (slash) photo
  // ids are tracked separately — they live in the HTML, not the card row,
  // and reach createNote as inlineMediaIds for permission linking.
  const [attachments, setAttachments] = useState<NoteMediaCard[]>(() =>
    editMode
      ? (initialMedia ?? []).map((m) => ({
          photoId: m.id,
          uri: null,
          contentType: m.contentType,
          fileName: m.fileName,
        }))
      : [],
  );
  // Footer uploads: optimistic, cancellable progress tiles that commit into
  // `attachments` on success. The pending-upload state machine (temp ids,
  // abort controllers, unmount cleanup) is shared with the photos custom field.
  const {
    pending,
    startUploads: startFooterUploads,
    cancel: cancelFooterUpload,
    reset: resetFooterUploads,
  } = usePendingMediaUploads({
    onUploaded: ({ photoId, objectUrl, contentType, fileName }) =>
      setAttachments((prev) => [
        ...prev,
        { photoId, uri: objectUrl, contentType, fileName },
      ]),
  });
  // Inline (slash-command) uploads in flight — counted only, since their
  // progress shows in the editor image node, not a footer tile.
  const [inlineUploadingCount, setInlineUploadingCount] = useState(0);
  // Send is disabled while ANY upload is in flight (footer or inline) so a note
  // can't post before its photos finish and silently drop them.
  const uploading = pending.length > 0 || inlineUploadingCount > 0;
  const inlineMediaIds = useRef<Set<string>>(new Set());
  // Abort controllers for in-flight inline (slash) uploads — aborted on unmount
  // (the footer path's equivalent lives in usePendingMediaUploads).
  const inlineControllers = useRef<Set<AbortController>>(new Set());
  const { uploadNotePhoto } = useNoteMediaUpload();
  const isEmpty = useMemo(() => isRichTextEmpty(content), [content]);
  const editorKeyRef = useRef(0);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorWrapRef = useRef<HTMLDivElement>(null);
  // Docked mode seats the editor's tools itself, so it holds the editor.
  const [editor, setEditor] = useState<Editor | null>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  // The docked bar also writes replies and edits, retargeted from the feed
  // (Reply / Edit on a comment) instead of opening a composer in place.
  const dock = useDockedComposer();
  const dockTarget = docked ? (dock?.target ?? null) : null;
  const dockEdit = dockTarget?.kind === "edit" ? dockTarget : null;
  const threadParentId =
    dockTarget?.kind === "reply" ? dockTarget.parentNoteId : parentNoteId;
  const editing = editMode || !!dockEdit;
  const editingNoteId = dockEdit?.noteId ?? editNoteId;
  // Edit mode: inline photo srcs in stored HTML are canonical content-route
  // paths — swap to authenticated object URLs so the editor displays them.
  // Usually instant (the note display already resolved + cached them). We mount
  // the editor only once `resolving` clears (below) so it starts from the
  // resolved HTML — remounting it afterwards would discard in-progress edits.
  const { html: resolvedInitial, resolving: resolvingInitial } =
    useResolvedMediaHtml(initialContent);
  const editorInitialContent = editMode ? resolvedInitial : "";
  const editorKey = editorKeyRef.current;

  useEffect(() => {
    const controllers = inlineControllers.current;
    return () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current);
      for (const controller of controllers) controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!focusRequest) return;
    const frame = requestAnimationFrame(() => {
      editorWrapRef.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [focusRequest]);

  // Mention-access guard: after the note posts, the globally-mounted
  // MentionAccessDialogMount checks the @-mentioned ids and pops the
  // "they don't have access" prompt when any of them can't see the
  // issue. The note itself never blocks on the check.
  const { promptIfMissing } = useMentionAccessActions();

  const placeholder = editing
    ? "Edit comment"
    : threadParentId
      ? "Write a reply"
      : "Write a comment";

  const submitLabel = editing ? "Save" : threadParentId ? "Reply" : "Send";

  const handleExpand = useCallback(() => {
    // Re-expanding mid-collapse: cancel the pending content swap to keep the editor.
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    setExpanded(true);
    setMountExpanded(true);
  }, []);

  // Animate closed, then swap content + reset the editor AFTER the shrink.
  const collapse = useCallback(() => {
    setExpanded(false);
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
    collapseTimer.current = setTimeout(() => {
      collapseTimer.current = null;
      setContent("");
      setAttachments([]);
      resetFooterUploads();
      inlineMediaIds.current.clear();
      editorKeyRef.current += 1;
      setMountExpanded(false);
    }, COLLAPSE_DURATION_MS);
  }, [resetFooterUploads]);

  const handleUpdate = useCallback((html: string) => {
    setContent(html);
  }, []);

  // Footer path: prompt for files (multi-select); the shared hook uploads each
  // on its own (optimistic tile with live preview + progress) and commits it to
  // `attachments` on success. Failed uploads toast (in the hook) and drop.
  // Notes accept images, video, PDF, and common office docs; the photos custom
  // field stays image-only (its own pickImageFiles path).
  const handleAttachPhoto = useCallback(async () => {
    await startFooterUploads(await pickFiles(NOTE_MEDIA_ACCEPT, true));
  }, [startFooterUploads]);

  const handleRemoveAttachment = useCallback((photoId: string) => {
    setAttachments((prev) => prev.filter((p) => p.photoId !== photoId));
  }, []);

  // Drag-reorder: the card row's order IS the submitted mediaIds order,
  // which the server persists as each photo's sort_order.
  const handleReorderAttachments = useCallback((mediaIds: string[]) => {
    setAttachments((prev) => {
      const byId = new Map(prev.map((p) => [p.photoId, p]));
      const next = mediaIds
        .map((id) => byId.get(id))
        .filter((p): p is NoteMediaCard => !!p);
      return next.length === prev.length ? next : prev;
    });
  }, []);

  // Slash path: same prompt + media upload; the editor inserts the returned
  // object URL as an image node, restored to the canonical content-route src
  // at submit. Inline uploads don't render a footer tile, so they aren't in the
  // pending-uploads hook — but they still get an abort controller so an
  // in-flight slash upload is cancelled if the composer unmounts.
  const uploadInlinePhoto = useCallback(async (file: File) => {
    const controller = new AbortController();
    inlineControllers.current.add(controller);
    setInlineUploadingCount((c) => c + 1);
    try {
      const uploaded = await uploadNotePhoto(file, { signal: controller.signal });
      // Inline picks are always images, so the preview object URL is present;
      // guard anyway to satisfy the editor's non-null src contract.
      if (!uploaded?.objectUrl) return null;
      inlineMediaIds.current.add(uploaded.photoId);
      return { objectUrl: uploaded.objectUrl };
    } finally {
      inlineControllers.current.delete(controller);
      setInlineUploadingCount((c) => c - 1);
    }
  }, [uploadNotePhoto]);

  const handlePickInlinePhoto = useCallback(async () => {
    const file = await pickImageFile();
    return file ? uploadInlinePhoto(file) : null;
  }, [uploadInlinePhoto]);

  // Dropped files: images go inline at the drop point (inline media are
  // image-only); anything else joins the footer attachments.
  const handleDropFile = useCallback(
    async (file: File) => {
      handleExpand();
      if (mediaKind(file.type) === "image") return uploadInlinePhoto(file);
      await startFooterUploads([file]);
      return null;
    },
    [handleExpand, uploadInlinePhoto, startFooterUploads],
  );

  const handleSubmit = useCallback(async () => {
    const hasAttachments = attachments.length > 0;
    if ((isEmpty && !hasAttachments) || submitting) return;

    setSubmitting(true);
    // Inline photos render from session object URLs — persist the canonical
    // /media/:id/content srcs instead (environment-independent, resolvable
    // by every viewer).
    const submitted = restoreMediaHtml(content);
    const mentionedIds = extractMentionedUserIds(submitted);
    try {
      if (editing && editingNoteId) {
        let mediaIds: string[] | undefined;
        if (!dockEdit) {
          const nextIds = attachments.map((p) => p.photoId);
          const initialIds = (initialMedia ?? []).map((m) => m.id);
          const mediaChanged =
            nextIds.length !== initialIds.length ||
            nextIds.some((id, i) => id !== initialIds[i]);
          if (mediaChanged) mediaIds = nextIds;
        }
        await updateNote(editingNoteId, submitted, mediaIds);
        // The docked bar hands back the draft it set aside for the edit.
        if (dockEdit) dock?.clear();
        else onCancel?.();
      } else {
        const footerIds = attachments.map((p) => p.photoId);
        const inlineIds = [...inlineMediaIds.current];
        await createNote({
          note: submitted,
          issueId,
          parentNoteId: threadParentId || null,
          mediaIds: footerIds.length ? footerIds : null,
          inlineMediaIds: inlineIds.length ? inlineIds : null,
        });
        if (docked) {
          // The bar stays up with the keyboard, the way Messages does after
          // Send: clear in place — a remount would blur the editor and drop
          // the keyboard.
          editor?.commands.clearContent(true);
          setContent("");
          setAttachments([]);
          resetFooterUploads();
          inlineMediaIds.current.clear();
          // A reply is one message: the bar returns to new comments (and a
          // bar called up off the Activity tab goes back down).
          if (dockTarget) dock?.clear();
        } else if (parentNoteId) {
          // Reply composers stay open — reset for the next reply.
          setContent("");
          setAttachments([]);
          resetFooterUploads();
          inlineMediaIds.current.clear();
          editorKeyRef.current += 1;
        } else {
          // Clear content synchronously before `finally` re-enables the button so
          // the empty-guard blocks a rapid second Send (would post a duplicate).
          setContent("");
          collapse();
        }
      }
      onSubmit?.();
      // Background check — fires AFTER the note is posted so a slow /
      // failing lookup never strands the comment.
      if (mentionedIds.length) {
        promptIfMissing({
          issueId,
          userIds: mentionedIds,
          viewerCanManageSharing: canManageItemSharing,
        });
      }
    } catch (error) {
      console.error("Failed to send note:", error);
    } finally {
      setSubmitting(false);
    }
  }, [
    content,
    attachments,
    isEmpty,
    submitting,
    editing,
    editingNoteId,
    initialMedia,
    dock,
    dockEdit,
    dockTarget,
    threadParentId,
    issueId,
    parentNoteId,
    createNote,
    updateNote,
    collapse,
    docked,
    editor,
    resetFooterUploads,
    onCancel,
    onSubmit,
    promptIfMissing,
    canManageItemSharing,
  ]);

  const handleCancel = useCallback(() => {
    if (editMode) {
      onCancel?.();
    } else {
      collapse();
    }
  }, [editMode, onCancel, collapse]);

  const handleMention = useCallback(() => {
    if (!editor) return;
    // The picker opens on "@" at a word start only; after a letter, space first.
    const before = editor.state.selection.$from.nodeBefore?.text ?? "";
    const trigger = before && !/\s$/.test(before) ? " @" : "@";
    editor.chain().focus().insertContent(trigger).run();
  }, [editor]);

  const leadingTools = useMemo<ToolbarButtonSpec[]>(
    () => [
      // An edit rewrites the text only; its attachments stay as posted.
      ...(photosEnabled && !dockEdit
        ? [{ icon: "image" as const, label: "Attach photo", action: handleAttachPhoto }]
        : []),
      { icon: "at-sign", label: "Mention", action: handleMention },
    ],
    [photosEnabled, dockEdit, handleAttachPhoto, handleMention],
  );

  const handleEditor = useCallback(
    (next: Editor | null) => {
      setEditor(next);
      if (docked) dock?.registerEditor(next);
    },
    [docked, dock],
  );

  // A bar called up off the Activity tab for a new comment goes back down
  // when the field is left empty; a draft keeps it standing.
  const dockTargetKind = dockTarget?.kind ?? null;
  useEffect(() => {
    if (!editor || dockTargetKind !== "comment") return;
    const handleBlur = () => {
      if (editor.isEmpty) dock?.clear();
    };
    editor.on("blur", handleBlur);
    return () => {
      editor.off("blur", handleBlur);
    };
  }, [editor, dockTargetKind, dock]);

  // A docked edit sets the draft aside, loads the comment (inline photos
  // resolved to viewable srcs first), and hands the draft back when the edit
  // saves or is dismissed. Loaded once per comment, so typing is never
  // overwritten by a late resolution.
  const { html: dockEditHtml, resolving: dockEditResolving } = useResolvedMediaHtml(
    dockEdit?.content ?? "",
  );
  const draftRef = useRef<string | null>(null);
  const loadedEditRef = useRef<string | null>(null);
  const dockEditId = dockEdit?.noteId ?? null;
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (dockEditId) {
      if (dockEditResolving || loadedEditRef.current === dockEditId) return;
      if (draftRef.current === null) draftRef.current = editor.getHTML();
      loadedEditRef.current = dockEditId;
      editor.commands.setContent(dockEditHtml, { emitUpdate: true });
      editor.commands.focus("end");
    } else if (loadedEditRef.current !== null) {
      loadedEditRef.current = null;
      editor.commands.setContent(draftRef.current ?? "", { emitUpdate: true });
      draftRef.current = null;
    }
  }, [editor, dockEditId, dockEditHtml, dockEditResolving]);

  // View-only access blocks every composer mode (new note, reply, edit) —
  // the backend rejects regardless, this just keeps the UI honest.
  if (!canComment) return null;

  if (docked && !editMode) {
    // A plain new comment needs no name above the field.
    const namedTarget = dockTarget?.kind === "comment" ? null : dockTarget;
    const canSend = dockEdit
      ? !isEmpty
      : !((isEmpty && attachments.length === 0) || uploading);
    return (
      <Box direction="column" gap="sm" fill="width">
        {/* What the bar is writing, when it isn't a new comment — the way
            Messages and Slack name a reply or an edit above the field. */}
        {namedTarget ? (
          <Box direction="row" align="center" gap="sm" paddingLeft="sm">
            <Icon
              name={namedTarget.kind === "reply" ? "reply" : "pen-line"}
              size="sm"
              color="muted"
            />
            <Box fill="flex" minWidth={0}>
              <Text size="xs" variant="muted" truncate={1}>
                {namedTarget.kind === "reply"
                  ? namedTarget.name
                    ? `Replying to ${namedTarget.name}`
                    : "Replying"
                  : "Editing comment"}
              </Text>
            </Box>
            <Button
              variant="ghost"
              size="icon-xs"
              shape="pill"
              aria-label={namedTarget.kind === "reply" ? "Cancel reply" : "Cancel edit"}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => dock?.clear()}
            >
              <Icon name="x" />
            </Button>
          </Box>
        ) : null}
        {toolsOpen && editor ? (
          <EditorFormatStrip editor={editor} leading={leadingTools} />
        ) : null}
        {!dockEdit && (attachments.length > 0 || pending.length > 0) && (
          <NoteMediaAttachments
            photos={attachments}
            pending={pending}
            onRemove={handleRemoveAttachment}
            onCancelPending={cancelFooterUpload}
            onReorder={handleReorderAttachments}
          />
        )}
        <Box direction="row" align="end" gap="sm">
          {/* The door to the tools: a press folds the strip out above the
              field (the circled + turns to a circled ×) without taking the keyboard's
              focus, the way Messages' "+" opens its row. */}
          <Button
            variant="ghost"
            size="icon-lg"
            shape="pill"
            aria-label="Formatting and attachments"
            aria-expanded={toolsOpen}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setToolsOpen((open) => !open)}
          >
            <Icon
              name="circle-plus"
              size="lg"
              className={
                toolsOpen
                  ? "rotate-45 transition-transform duration-200 motion-reduce:transition-none"
                  : "transition-transform duration-200 motion-reduce:transition-none"
              }
            />
          </Button>
          <Box
            ref={editorWrapRef}
            direction="row"
            align="end"
            fill="flex"
            minWidth={0}
            border
            rounded="2xl"
            background="card"
            // Lines scrolled past the cap clip inside the rounded edge
            // instead of crossing its border. `clip`, never `hidden`: a
            // hidden box is still a scroll container, and the editor's
            // scroll-to-caret nudges it — the offset outlives the lines
            // that caused it and leaves the text and Send riding high.
            overflow="clip"
          >
            {/* Grows line by line to five, then scrolls inside itself. */}
            <Box fill="flex" minWidth={0} maxHeight={DOCKED_MAX_HEIGHT} overflow="auto">
              <RichTextEditor
                label={ariaLabel}
                key={editorKey}
                content=""
                onUpdate={handleUpdate}
                onEditor={handleEditor}
                placeholder={placeholder}
                shortcutHints
                mentions
                compact
                fitContent
                toolbar={false}
                autoFocus={startExpanded}
                contentPaddingX="md"
                contentPaddingY="sm"
                onPickPhoto={photosEnabled && !dockEdit ? handlePickInlinePhoto : undefined}
              />
            </Box>
            <Box padding="xs" shrink={false}>
              <Button
                size="icon-sm"
                shape="pill"
                aria-label={submitLabel}
                // Send keeps the editor's focus, so the keyboard stays up
                // for the next comment.
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSubmit}
                disabled={!canSend || submitting}
                loading={submitting}
              >
                <Icon name={dockEdit ? "check" : "arrow-up"} size="md" />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // Edit mode renders frameless: the host note card owns the border/background.
  if (editMode) {
    return (
      <Box>
        <Box maxHeight={400} overflow="auto">
          {/* Mount only once inline-photo resolution settles, so the editor
              starts from the resolved HTML. Remounting it afterwards (the old
              key-flip approach) discarded edits typed during resolution. */}
          {!resolvingInitial && (
            <RichTextEditor
              label="Comment"
              key={editorKey}
              content={editorInitialContent}
              onUpdate={handleUpdate}
              placeholder={placeholder}
              shortcutHints
              mentions
              compact
              mediaThumbnails
              fitContent
              autoFocus
              onPickPhoto={photosEnabled ? handlePickInlinePhoto : undefined}
              onDropFile={photosEnabled ? handleDropFile : undefined}
            />
          )}
        </Box>
        <NoteMediaAttachments
          photos={attachments}
          pending={pending}
          onRemove={handleRemoveAttachment}
          onCancelPending={cancelFooterUpload}
          onReorder={handleReorderAttachments}
        />
        <Box
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          paddingTop="sm"
        >
          <Box direction="row" gap="xs">
            {photosEnabled && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleAttachPhoto}
                aria-label="Attach photo"
              >
                <Icon name="image" />
              </Button>
            )}
          </Box>
          <Box direction="row" gap="sm" align="center">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={
                (isEmpty && attachments.length === 0) || submitting || uploading
              }
              loading={submitting}
            >
              {submitLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // One persistent root for both states so the collapse/expand height change is
  // a CSS transition on the same element.
  return (
    <Box
      border
      rounded="lg"
      // Fill only — `surface` would add a card shadow and float the composer
      // off the panel; it is a flat bordered field.
      background="card"
      overflow="hidden"
      height={expanded ? "auto" : COLLAPSED_HEIGHT}
      className={COLLAPSE_TRANSITION}
    >
      {/* The editor stays mounted while collapsed so the first tap lands in it:
          iOS raises the keyboard only for a focus inside the tap itself. */}
      <Box ref={editorWrapRef} maxHeight={400} overflow="auto" onFocus={handleExpand}>
        <RichTextEditor
          label={ariaLabel}
          key={editorKey}
          content={editorInitialContent}
          onUpdate={handleUpdate}
          placeholder={placeholder}
          shortcutHints
          mentions
          compact
          mediaThumbnails
          autoFocus={startExpanded}
          contentPaddingX="md"
          contentPaddingY="sm"
          onPickPhoto={photosEnabled ? handlePickInlinePhoto : undefined}
          onDropFile={photosEnabled ? handleDropFile : undefined}
        />
      </Box>
      {mountExpanded ? (
        <>
          {(attachments.length > 0 || pending.length > 0) && (
            <Box paddingX="md" paddingTop="xs" paddingBottom="sm">
              <NoteMediaAttachments
                photos={attachments}
                pending={pending}
                onRemove={handleRemoveAttachment}
                onCancelPending={cancelFooterUpload}
                onReorder={handleReorderAttachments}
              />
            </Box>
          )}
          <Box
            direction="row"
            align="center"
            justify="between"
            padding="sm"
          >
            <Box direction="row" gap="xs">
              {photosEnabled && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={handleAttachPhoto}
                  aria-label="Attach photo"
                >
                  <Icon name="image" />
                </Button>
              )}
            </Box>
            <Box direction="row" gap="sm" align="center">
              {(editMode || expanded) && (
                <Button variant="ghost" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={
                  (isEmpty && attachments.length === 0) ||
                  submitting ||
                  uploading
                }
                loading={submitting}
              >
                {submitLabel}
              </Button>
            </Box>
          </Box>
        </>
      ) : null}
    </Box>
  );
}

// Five 20px lines plus the field's 8px insets — then the field scrolls.
const DOCKED_MAX_HEIGHT = 116;

// Explicit height gives the transition a concrete start/end point.
const COLLAPSED_HEIGHT = 38;

// The CSS transition (duration-200 on the root) and the JS swap timer read the
// same number so they can't drift. `interpolate-size` is what lets the
// expanded `height: auto` animate at all (Chrome 129+; snaps elsewhere).
const COLLAPSE_DURATION_MS = 200;
const COLLAPSE_TRANSITION =
  "transition-[height] duration-200 ease-in-out [interpolate-size:allow-keywords]";
